import { Icon } from '@blueprintjs/core';
import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderSwitchFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderSwitchFilter';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import {
  NUMBER_LIKE_FILTER_MAP,
  TEXT_FILTER_MAP,
  TEXT_LIKE_FILTER_MAP,
} from 'Components/Generic/AdvancedDataTable/Helper';
import BOSFilterMultiselect from 'Components/Generic/BOSMultiselect/BOSFilterMultiselect';
import OrganizationMultiselect from 'Components/Generic/BOSMultiselect/OrganizationMultiselect';
import UserMultiselect from 'Components/Generic/BOSMultiselect/UserMultiselect';
import VendorMultiselect from 'Components/Generic/BOSMultiselect/VendorMultiselect';
import BOSSelect from 'Components/Generic/BOSSelect';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import FilterIndicator from 'Components/Generic/Misc/FilterIndicator';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import UserNameWithDrawerButton from 'Components/SiteDrawerContainer/UserNameWithDrawerButton';
import VendorNameWithDrawerButton from 'Components/SiteDrawerContainer/VendorNameWithDrawerButton';
import { SITE_PAGES } from 'Config';
import AppData from 'Data/AppData';
import { useAlertContext } from 'Data/context/Alerts';
import { useGlobalState } from 'Data/context/GlobalState';
import { getOrgNameFromId, getUserOrgIds } from 'Data/context/Organizations';
import { getUserNameFromId } from 'Data/context/Users';
import { getVendorNameFromId } from 'Data/context/Vendors';
import { user_roles } from 'Data/Enum';
import { TransactionData } from 'Data/TransactionData';
import DataManager from 'Lib/DataManager';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import {
  CHARGE_ALLOCATION_STATUS,
  getTransactionCategory,
  getTransactionType,
  TRANSACTION_CATEGORY,
  TRANSACTION_TYPE,
} from 'Pages/Transactions/Enum';
import TransactionLanding from 'Pages/Transactions/TransactionLanding';

import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { canUserViewAllCores, isUserAuthorized } from 'Util';
import { formatDate } from 'Util/dateUtil';
import { getTagValue } from 'Util/tagUtil';

let DM = new DataManager();

let filterOptions = {
  id: {
    seqRestKey: '*id',
    mapToSeqRestValue: NUMBER_LIKE_FILTER_MAP,
  },
  notes: {
    seqRestKey: '*notes',
    mapToSeqRestValue: TEXT_LIKE_FILTER_MAP,
  },
  organization: {
    seqRestKey: '*parent_organization_id',
  },
  vendor: {
    seqRestKey: '*vendor_id',
  },
  reference_number: {
    seqRestKey: '*reference_number',
    mapToSeqRestValue: TEXT_LIKE_FILTER_MAP,
  },
  requested_by: {
    seqRestKey: '*requested_by',
  },
  created_by: {
    seqRestKey: '*created_by',
  },
  purchased_by: {
    seqRestKey: '*purchased_by',
  },
  transaction_type: {
    seqRestKey: '*transaction_type',
  },
  is_service_contract: {
    seqRestKey: '*tags.is_service_contract',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  category: {
    seqRestKey: '*category',
  },
  expense_code: {
    seqRestKey: '*$charge_allocations.expense_code.expense_code',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
};

const TransactionListView = ({ includeDeleteColumn = false }) => {
  const [globalState] = useGlobalState();
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const [data, setData] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [persistentFilter, updateFilterField] =
    usePersistentFiltering(`TRANS_FILTER`);

  let title = (
    <span>
      All Transactions <FilterIndicator />
    </span>
  );

  useEffect(() => {
    DM.add(
      TransactionData.registerListener('LIST_LOADED', setData),
      TransactionData,
    );
    DM.add(
      TransactionData.registerListener('LIST_LOAD_ERROR', onListLoadError),
      TransactionData,
    );
    DM.add(
      TransactionData.registerListener('RECORDS_DELETED', onTransactionDeleted),
      TransactionData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    refreshData();
  }, [globalState?.fiscalYear]);

  const onListLoadError = () => {};
  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };
  const deleteTransaction = (trans) => {
    TransactionData.deleteTransaction(trans);
  };

  const deleteClicked = (trans) => {
    openDeleteDialog({
      objectType: 'Transaction',
      onConfirmDelete: () => deleteTransaction(trans),
      dialogMessage: (
        <ul style={{ listStyle: 'disc' }}>
          <li>Line item(s) will be unassigned.</li>
          <li>Associated charge allocation(s) will be deleted.</li>
        </ul>
      ),
    });
  };

  const onTransactionDeleted = () => {
    showDeletedToast('Transaction');
    refreshData();
  };

  const headers = [
    {
      label: 'ID',
      value: 'id',
      width: 80,
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'ID'}
            defaultValue={persistentFilter?.id}
            onFilterValueUpdate={(e) => {
              updateFilterField('id', e.target.value);
            }}
          />
        );
      },
      render(trans) {
        return <Link to={`./${trans.id}`}>{trans.id}</Link>;
      },
    },

    {
      label: 'Ref #',
      value: 'reference_number',
      width: 112,
      filterable: true,
      sortable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Ref #'}
            defaultValue={persistentFilter?.reference_number}
            onFilterValueUpdate={(e) =>
              updateFilterField('reference_number', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Date',
      value: 'date_purchased',
      format: formatDate,
      width: 96,
      sortable: true,
    },

    {
      label: 'Core Facility',
      value: 'organization',
      width: 144,
      sortable: true,
      sortFieldName: 'organizations.name',
      filterable: true,
      renderHeader() {
        return (
          <OrganizationMultiselect
            onSelectedOrganizationsUpdate={(orgList) =>
              updateFilterField('organization', orgList)
            }
            defaultSelectedItems={persistentFilter?.organization}
            isHeader
          />
        );
      },
      format: (organization) => {
        return <OrgNameWithDrawerButton orgId={organization.id} />;
      },
      getText: (transaction) => {
        return getOrgNameFromId(transaction.parent_organization_id);
      },
    },

    {
      label: 'Requester',
      value: 'requested_by',
      width: 140,
      filterable: true,
      renderHeader() {
        return (
          <UserMultiselect
            labelText='Requester'
            onSelectedUsersUpdate={(userList) =>
              updateFilterField('requested_by', userList)
            }
            defaultSelectedItems={persistentFilter?.requested_by}
            orgIds={!canUserViewAllCores() && getUserOrgIds()}
            isHeader
          />
        );
      },
      format: (requested_by) => {
        return <UserNameWithDrawerButton userId={requested_by} />;
      },
      getText: (transaction) => {
        return getUserNameFromId(transaction.requested_by);
      },
    },

    {
      label: 'Purchaser',
      value: 'purchased_by',
      width: 140,
      filterable: true,
      renderHeader() {
        return (
          <UserMultiselect
            labelText='Purchaser'
            onSelectedUsersUpdate={(userList) =>
              updateFilterField('purchased_by', userList)
            }
            defaultSelectedItems={persistentFilter?.purchased_by}
            userRoles={[user_roles.BUYER.key, user_roles.TRANSACTOR.key]}
            isHeader
          />
        );
      },
      format: (purchased_by) => {
        return <UserNameWithDrawerButton userId={purchased_by} />;
      },
      getText: (transaction) => {
        return getUserNameFromId(transaction.purchased_by);
      },
    },

    {
      label: 'Vendor',
      value: 'vendor',
      width: 160,
      sortable: true,
      sortFieldName: 'vendors.name',
      filterable: true,
      renderHeader() {
        return (
          <VendorMultiselect
            onSelectedVendorsUpdate={(vendorList) =>
              updateFilterField('vendor', vendorList)
            }
            defaultSelectedItems={persistentFilter?.vendor}
            isHeader
          />
        );
      },
      format: (vendor) => {
        return <VendorNameWithDrawerButton vendorId={vendor.id} />;
      },
      getText: (transaction) => {
        return getVendorNameFromId(transaction.vendor_id);
      },
    },

    {
      label: 'Description',
      value: 'notes',
      filterable: true,
      truncated: true,
      minWidth: 150,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Description'}
            defaultValue={persistentFilter?.notes}
            onFilterValueUpdate={(e) =>
              updateFilterField('notes', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Amount',
      value: 'display_amount',
      width: 100,
      moneyColumn: true,
    },

    {
      label: 'Category',
      value: 'category',
      width: 112,
      filterable: true,
      renderHeader() {
        return (
          <BOSSelect
            listData={Object.entries(TRANSACTION_CATEGORY).map(
              ([key, value]) => {
                return {
                  id: key,
                  name: value,
                };
              },
            )}
            labelText='Category'
            onItemSelect={(item) => updateFilterField('category', [item])}
            defaultSelectedId={
              persistentFilter?.category?.length
                ? persistentFilter.category[0].id
                : undefined
            }
            isHeader
          />
        );
      },
      format: (category) => {
        return getTransactionCategory(category);
      },
    },

    {
      label: 'Type',
      value: 'transaction_type',
      width: 108,
      filterable: true,
      renderHeader() {
        return (
          <BOSFilterMultiselect
            listData={Object.entries(TRANSACTION_TYPE).map(([key, value]) => {
              return {
                key,
                value,
              };
            })}
            labelText='Type'
            useKeyValueFields
            onSelectionUpdate={(typeList) =>
              updateFilterField('transaction_type', typeList)
            }
            defaultSelectedItems={persistentFilter?.transaction_type}
            autofocus
            isHeader
          />
        );
      },
      format: (type) => {
        return getTransactionType(type);
      },
    },

    {
      label: 'Service Contract',
      value: 'is_service_contract',
      fieldName: 'tags',
      width: 80,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderSwitchFilter
            fieldName={'Is SC'}
            onFilterValueUpdate={(val) =>
              updateFilterField('is_service_contract', val ? val : 'false')
            }
          />
        );
      },
      render(transaction) {
        return (
          getTagValue(transaction, 'is_service_contract') && (
            <Icon size='20' icon={'tick'} />
          )
        );
      },
    },

    {
      label: 'Exp Code',
      value: 'expense_code',
      fieldName: 'charge_allocations',
      width: 100,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Exp Code'}
            defaultValue={persistentFilter?.expense_code}
            onFilterValueUpdate={(e) =>
              updateFilterField('expense_code', e.target.value)
            }
          />
        );
      },
      format: (charge_allocations) => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              paddingTop: 4,
              paddingBottom: 4,
            }}>
            {charge_allocations
              .map((detail) => detail.expense_code)
              .filter(
                (obj1, i, arr) =>
                  arr.findIndex((obj2) => obj2.id === obj1.id) === i,
              )
              .sort((a, b) => a.expense_code.localeCompare(b.expense_code))
              .map((ec) => ec.expense_code)
              .join(', ')}
          </div>
        );
      },
      getText: (sc) =>
        sc.charge_allocations
          ? sc.charge_allocations
              .map((detail) => detail.expense_code)
              .filter(
                (obj1, i, arr) =>
                  arr.findIndex((obj2) => obj2.id === obj1.id) === i,
              )
              .sort((a, b) => a.expense_code.localeCompare(b.expense_code))
              .map((ec) => ec.expense_code)
              .join(', ')
          : '',
    },

    ...(includeDeleteColumn
      ? [
          {
            label: '',
            value: '',
            isIconCol: true,
            render(transaction) {
              return (
                (isUserAuthorized({
                  roles: [user_roles.ADMINISTRATOR],
                }) ||
                  transaction.created_by === AppData.userID() ||
                  transaction.purchased_by === AppData.userID()) && (
                  <DeleteButton
                    miniVersion
                    onDeleteItem={() => {
                      deleteClicked(transaction);
                    }}
                    tooltipText={'Delete Transaction'}
                  />
                )
              );
            },
          },
        ]
      : []),
  ];

  const shouldHighlightTransaction = (trans) => {
    return (
      trans?.charge_allocations?.some(
        (ca) => ca.status === CHARGE_ALLOCATION_STATUS.UNREVIEWED.key,
      ) || false
    );
  };

  let breadcrumb = [
    SITE_PAGES.TRANSACTION_LANDING,
    SITE_PAGES.ALL_TRANSACTIONS,
  ];

  let pageContent = (
    <AdvancedDataTable
      headers={headers}
      refreshKey={refreshKey}
      title={title}
      dataKey={'TRANS'}
      data={data}
      dataQueryFunc={TransactionData.getTransactionListFunction(
        globalState.fiscalYear,
      )}
      exportDataFunc={TransactionData.getExportTransactionFunction(
        globalState.fiscalYear,
      )}
      filterOptions={filterOptions}
      filterBy={persistentFilter}
      updateFilterField={updateFilterField}
      highlightFunc={shouldHighlightTransaction}
    />
  );

  return (
    <TransactionLanding breadcrumbs={breadcrumb}>
      {pageContent}
    </TransactionLanding>
  );
};

TransactionListView.propTypes = {
  includeDeleteColumn: PropTypes.bool,
};

export default TransactionListView;
